import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, StyleSheet, View } from 'react-native';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { assets } from '@assets';
import { LiveControlsTestIDs, livePageTestIDs, Theme } from '@constants';
import { helpers } from '@helpers';
import { useAppContextReference, useLiveStreamSelector, useNodeHandle } from '@hooks';
import { anfState, userSettingsState } from '@store';

import { FocusableFlatList } from '../../focusable/FocusableFlatList/FocusableFlatList';
import { MenuContainer } from '../../MenuContainer/MenuContainer';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { segmentEventTypes } from '../../segment/segmentEventTypes';
import { segmentTrackEvent } from '../../segment/SegmentTrackEvent';
import { ANFTile, ANFTileLayout } from '../../tiles/ANFTile/ANFTile';
import { ContextDrawerTile } from '../../tiles/ContextDrawerTile/ContextDrawerTile';

// Layout constants for this component
const Layout = {
  leftMargin: Theme.px(90),
  rightMargin: Theme.px(118),
  topMargin: Theme.px(330),
  scheduleTextSpacing: Theme.px(6),
};

export const ContextDrawer = ({ data, animateValue, nextFocusUp, showControls }) => {
  const tPath = 'videoControls';
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const liveControlsFocusReference = useAppContextReference(LiveControlsTestIDs.liveVideoControls);

  const contextDrawerTileHandle = useNodeHandle(liveControlsFocusReference);
  const contextDrawerSpanishTileReference = useRef();
  const contextDrawerSpanishTileHandle = useNodeHandle(contextDrawerSpanishTileReference);
  const contextDrawerPattrnTileReference = useRef();
  const contextDrawerPattrnTileHandle = useNodeHandle(contextDrawerPattrnTileReference);

  const setANF = useSetRecoilState(anfState);

  const { setCurrentLiveVideoStream, liveVideoStreamTypes, liveVideoState } = useLiveStreamSelector();
  const { currentLiveVideoStream } = liveVideoState;
  const userSettings = useRecoilValue(userSettingsState);

  const [liveFeedSchedule, liveFeedSpanishSchedule, livePattrnSchedule] = data?.schedule;

  const schedules = {
    [liveVideoStreamTypes.videoStream]: liveFeedSchedule,
    [liveVideoStreamTypes.videoStreamSpanish]: liveFeedSpanishSchedule,
    [liveVideoStreamTypes.videoStreamPattrn]: livePattrnSchedule,
  };

  const anfAlerts = data?.anf?.filter(anf => anf.url);
  const hasANF = anfAlerts && anfAlerts.length > 0;
  const anfSelected = anfAlerts?.find(anf => anf.selected);

  const watchingTitle = anfSelected ? anfSelected?.title : schedules[currentLiveVideoStream]?.name;

  const videoCompleted = type => {
    const isANF = Boolean(anfSelected);

    if (anfSelected || type !== currentLiveVideoStream) {
      segmentTrackEvent(segmentEventTypes.VIDEO_PLAYBACK_COMPLETED, {
        ...data,
        ...userSettings,
        isANF,
      });
    }
  };

  const handleANFStreamSelection = (url, type) => {
    videoCompleted(type);
    setANF(anfData => {
      const newANFAlerts = anfData?.data?.map(anf => ({ ...anf, selected: anf.url === url }));

      return {
        ...anfData,
        data: newANFAlerts,
      };
    });
  };

  const handleLiveVideoStreamSelection = type => {
    handleANFStreamSelection(null, type);
    setCurrentLiveVideoStream(type);
    showControls?.();
  };

  const anfListReference = useRef();
  const anfListHandle = useNodeHandle(anfListReference);

  const _getItemLayout = useCallback(
    (_, index) => ({
      length: Theme.px(ANFTileLayout.width),
      offset: Theme.px(ANFTileLayout.width * index),
      index,
    }),
    [],
  );

  const onFocus = () => {
    showControls?.();
  };

  const onPress = streamType => () => handleLiveVideoStreamSelection(streamType);

  const _renderItem = ({ url, title, selected, description }, index) => {
    const length = anfAlerts?.length ?? 0;
    const viewMore = index + 1 < length ? t('liveANFTile.accessibilityHintMoreItems') : '';

    return (
      <ANFTile
        accessibilityHint={t(selected ? 'liveANFTile.accessibilityHintWatching' : 'liveANFTile.accessibilityHint', {
          watching: watchingTitle,
          viewMore,
          title: title ?? t('anfDefaultTitle'),
        })}
        accessibilityLabel={t('liveANFTile.accessibilityLabel', {
          title: title ?? t('anfDefaultTitle'),
          description: description ?? '',
          index: index + 1,
          length,
        })}
        description={description ?? ''}
        selected={selected}
        title={title ?? t('anfDefaultTitle')}
        onPress={() => {
          showControls?.();
          if (!selected) {
            handleANFStreamSelection(url, null);
          }
        }}
      />
    );
  };

  const anfHint = (anfAlerts ?? []).length > 0 ? t('liveTWCTile.hasANFHint') : '';
  const anfHintSpanish = (anfAlerts ?? []).length > 0 ? t('liveTWCTileSpanish.hasANFHint') : '';

  const tilesConfig = {
    liveStream: {
      accessibilityHint: {
        accessabilityHint: 'liveTWCTile.accessibilityHint',
        accessabilityHintWatching: 'liveTWCTile.accessibilityHintWatching',
        anfHint,
      },
      accessibilityLabel: {
        accessabilityLabel: 'liveTWCTile.accessibilityLabel',
        accessabilityLabelWatching: 'liveTWCTile.accessibilityLabelWatching',
      },
      backgroundImage: assets.videoStreamBackground,
      tileData: liveFeedSchedule,
      fallbackLogo: assets.logo,
      liveFeedSelected: !anfSelected && currentLiveVideoStream === liveVideoStreamTypes.videoStream,
      nextFocusDown: helpers.isAndroid ? contextDrawerTileHandle : null,
      nextFocusLeft: helpers.isAndroid ? contextDrawerTileHandle : null,
      nextFocusRight: contextDrawerSpanishTileHandle,
      ref: liveControlsFocusReference,
      testID: livePageTestIDs.contextDrawerTile,
      streamType: liveVideoStreamTypes.videoStream,
    },
    liveStreamSpanish: {
      accessibilityHint: {
        accessabilityHint: 'liveTWCTileSpanish.accessibilityHint',
        accessabilityHintWatching: 'liveTWCTileSpanish.accessibilityHintWatching',
        anfHint: anfHintSpanish,
      },
      accessibilityLabel: {
        accessabilityLabel: 'liveTWCTileSpanish.accessibilityLabel',
        accessabilityLabelWatching: 'liveTWCTileSpanish.accessibilityLabelWatching',
      },
      backgroundImage: assets.videoStreamSpanishBackground,
      tileData: liveFeedSpanishSchedule,
      fallbackLogo: null,
      liveFeedSelected: !anfSelected && currentLiveVideoStream === liveVideoStreamTypes.videoStreamSpanish,
      nextFocusDown: helpers.isAndroid ? contextDrawerSpanishTileHandle : null,
      nextFocusLeft: contextDrawerTileHandle,
      nextFocusRight: contextDrawerPattrnTileHandle,
      ref: contextDrawerSpanishTileReference,
      testID: livePageTestIDs.contextDrawerSpanishTile,
      streamType: liveVideoStreamTypes.videoStreamSpanish,
    },
    liveStreamPattrn: {
      accessibilityHint: {
        accessabilityHint: 'livePattrnTile.accessibilityHint',
        accessabilityHintWatching: 'livePattrnTile.accessibilityHintWatching',
        anfHint,
      },
      accessibilityLabel: {
        accessabilityLabel: 'livePattrnTile.accessibilityLabel',
        accessabilityLabelWatching: 'livePattrnTile.accessibilityLabelWatching',
      },
      backgroundImage: assets.videoStreamPattrn,
      tileData: livePattrnSchedule,
      fallbackLogo: assets.logoPattrn,
      liveFeedSelected: !anfSelected && currentLiveVideoStream === liveVideoStreamTypes.videoStreamPattrn,
      nextFocusDown: helpers.isAndroid ? contextDrawerPattrnTileHandle : null,
      nextFocusLeft: contextDrawerSpanishTileHandle,
      nextFocusRight: helpers.isAndroid ? contextDrawerPattrnTileHandle : null,
      ref: contextDrawerPattrnTileReference,
      testID: livePageTestIDs.contextDrawerPattrnTile,
      streamType: liveVideoStreamTypes.videoStreamPattrn,
    },
  };

  return (
    <>
      <Animated.View
        collapsable={false}
        style={[
          styles.background,
          {
            transform: [
              {
                translateY: animateValue.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-Theme.appHeight, 0],
                }),
              },
            ],
          },
        ]}
        testID={LiveControlsTestIDs.liveControlsMenuUnderlay}
        useNativeDriver
      >
        {/* Show menu gradient and turn off gradient from menu to make sure the gradient shows under
        the context drawer */}
        <MenuContainer useGradient />
      </Animated.View>
      <Animated.View
        collapsable={false}
        style={[
          styles.background,
          {
            transform: [
              {
                translateY: animateValue.interpolate({
                  inputRange: [0, 1],
                  outputRange: [Theme.appHeight, 0],
                }),
              },
            ],
          },
        ]}
        testID={LiveControlsTestIDs.background}
        useNativeDriver
      >
        <View style={styles.container}>
          <View style={styles.anfFeedsContainer}>
            <TWCText
              importantForAccessibility={'no'}
              style={styles.anfRowTitle}
              testID={LiveControlsTestIDs.currentShowTitle}
              variant={'mediumText30'}
            >
              {hasANF ? t('anfRow') : ''}
            </TWCText>
            <FocusableFlatList
              data={anfAlerts ?? []}
              focusIndexChanged={onFocus}
              focusPositions={3}
              getItemLayout={_getItemLayout}
              horizontal
              importantForAccessibility={anfAlerts ? 'yes' : 'no'}
              initialNumToRender={4}
              itemUnderlayColor={'clear'}
              keyExtractor={(_, index) => `${index}-anfTile`}
              nextFocusDown={contextDrawerTileHandle}
              nextFocusLeft={null}
              nextFocusRight={null}
              nextFocusUp={nextFocusUp}
              ref={anfListReference}
              renderItem={_renderItem}
              shouldUseContextReference={false}
              showsHorizontalScrollIndicator={false}
              testID={LiveControlsTestIDs.liveControlsANFTileList}
              wrapItem={false}
              paddingEnd={Layout.rightMargin}
            />
          </View>
          <View style={styles.liveFeedsWrapper}>
            <View style={styles.liveFeedsContainer}>
              <TWCText
                importantForAccessibility={'no'}
                style={styles.liveFeedTitle}
                testID={LiveControlsTestIDs.currentShowTitle}
                variant={'mediumText30'}
              >
                {t('liveChannels')}
              </TWCText>

              <View style={styles.row}>
                {Object.values(tilesConfig).map(
                  ({
                    accessibilityHint,
                    accessibilityLabel,
                    backgroundImage,
                    tileData,
                    fallbackLogo,
                    liveFeedSelected,
                    nextFocusDown,
                    nextFocusLeft,
                    nextFocusRight,
                    ref,
                    testID,
                    streamType,
                  }) => (
                    <ContextDrawerTile
                      accessibilityHint={t(
                        !anfSelected && currentLiveVideoStream === streamType
                          ? accessibilityHint.accessabilityHintWatching
                          : accessibilityHint.accessabilityHint,
                        {
                          anfHint: accessibilityHint.anfHint,
                        },
                      )}
                      accessibilityLabel={t(
                        !anfSelected && currentLiveVideoStream === streamType
                          ? accessibilityLabel.accessabilityLabelWatching
                          : accessibilityLabel.accessabilityLabel,
                        {
                          title: liveFeedSchedule?.name,
                          watching: watchingTitle,
                        },
                      )}
                      backgroundImage={backgroundImage}
                      data={tileData}
                      fallbackLogo={fallbackLogo}
                      liveFeedSelected={liveFeedSelected}
                      nextFocusDown={nextFocusDown}
                      nextFocusLeft={nextFocusLeft}
                      nextFocusRight={nextFocusRight}
                      nextFocusUp={hasANF ? anfListHandle : nextFocusUp}
                      ref={ref}
                      testID={testID}
                      onFocus={onFocus}
                      onPress={onPress(streamType)}
                    />
                  ),
                )}
              </View>
            </View>
          </View>
        </View>
      </Animated.View>
    </>
  );
};

const styles = StyleSheet.create({
  anfFeedsContainer: {
    flexDirection: 'column',
    minHeight: Theme.px(230),
  },
  anfRowTitle: {
    marginBottom: Theme.px(11),
    marginLeft: Theme.px(20),
  },
  background: {
    flex: 1,
    height: Theme.appHeight,
    position: 'absolute',
    width: Theme.appWidth,
  },
  container: {
    flexDirection: 'column',
    marginLeft: Layout.leftMargin,
    marginTop: Layout.topMargin,
    width: Theme.appWidth - Layout.leftMargin - Layout.rightMargin,
  },
  liveFeedTitle: { marginLeft: Theme.px(20) },
  liveFeedsContainer: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    marginTop: Theme.px(68),
  },
  liveFeedsWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  row: { flexDirection: 'row', marginTop: Theme.px(11) },
});
